// What a printed book's design is made of: the look it started from, each choice in it, and any pages given choices of
// their own (a photo book's page by the photo it starts with, a cookbook's or a set of cards' by its recipe). The
// builders (src/lib/print/looks.ts for photo books, src/lib/cookbook/looks.ts for cookbooks, src/lib/cookbook/cardLooks.ts
// for recipe cards) are made from these lists, and the Worker checks a design against them before keeping it with its
// book (albums.print_design, cookbooks.print_design, cookbooks.cards_design).

// How the photos print, whatever colour they are in the app: as they are, in black and white, or in sepia.
const PHOTOS = ['color', 'mono', 'sepia'] as const

// Choices added after designs were first kept, and what a design kept before them chose without knowing.
const ADDED: Record<string, unknown> = { photos: 'color' }

export const PHOTO_BOOK = {
  looks: ['classic', 'gallery', 'bleed', 'collage'],
  most: [1, 2, 3, 4],
  edges: ['bleed', 'thin', 'border', 'wide'],
  type: ['modern', 'traditional'],
  captions: [true, false],
  paper: ['white', 'natural'],
  cover: ['photo', 'window', 'words'],
  photos: PHOTOS,
} as const

export const COOKBOOK = {
  looks: ['classic', 'gallery', 'bleed', 'kitchen'],
  layout: ['facing', 'one'],
  pictures: ['bleed', 'framed'],
  type: ['traditional', 'modern'],
  paper: ['white', 'natural'],
  cover: ['plate', 'photo', 'words'],
  photos: PHOTOS,
} as const

// A cookbook's recipes as a set of cards, each with a picture or its name on the front and the recipe on the back.
export const RECIPE_CARDS = {
  looks: ['original', 'dish', 'framed', 'words'],
  front: ['card', 'dish', 'title'],
  edges: ['bleed', 'border'],
  type: ['traditional', 'modern'],
  paper: ['white', 'natural'],
  photos: PHOTOS,
} as const

// What a single page (or card) can change about itself.
export const PHOTO_PAGE = { most: PHOTO_BOOK.most, edges: PHOTO_BOOK.edges } as const
export const COOKBOOK_RECIPE = { layout: COOKBOOK.layout, pictures: COOKBOOK.pictures } as const
export const RECIPE_CARD = { front: RECIPE_CARDS.front, edges: RECIPE_CARDS.edges } as const

type Options = Record<string, readonly (string | number | boolean)[]>
export type PageEdits<P extends Options> = Record<string, Partial<{ [K in keyof P]: P[K][number] }>>
// More pages than any book holds; past this a design is refused rather than kept.
const MOST_EDITS = 500
const PAGE_KEY = /^[\w-]{1,64}$/

// Pages' own choices read back: each page named by a plain id, with one or more of the choices a page can change.
function readEdits(each: Options, value: unknown): PageEdits<Options> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const entries = Object.entries(value)
  if (entries.length > MOST_EDITS) return null
  for (const [key, edit] of entries) {
    if (!PAGE_KEY.test(key) || !edit || typeof edit !== 'object' || Array.isArray(edit)) return null
    const choices = Object.entries(edit)
    const known = ([k, v]: [string, unknown]) => Object.hasOwn(each, k) && (each[k] as readonly unknown[]).includes(v)
    if (!choices.length || !choices.every(known)) return null
  }
  return Object.fromEntries(entries) as PageEdits<Options>
}

// A design read back: its look and every choice, each one of the known options, or nothing at all.
export function readDesign<T extends Options & { looks: readonly string[] }, P extends Options = Record<never, never>>(
  options: T,
  value: unknown,
  each?: P,
): {
  from: T['looks'][number]
  look: { [K in Exclude<keyof T, 'looks'>]: T[K][number] }
  pages?: PageEdits<P>
} | null {
  if (!value || typeof value !== 'object') return null
  const { from, look, pages } = value as { from?: unknown; look?: unknown; pages?: unknown }
  if (!options.looks.includes(from as string) || !look || typeof look !== 'object') return null
  const keys = Object.keys(options).filter((k) => k !== 'looks')
  const given = { ...Object.fromEntries(keys.filter((k) => k in ADDED).map((k) => [k, ADDED[k]])), ...look }
  if (Object.keys(given).length !== keys.length) return null
  if (!keys.every((k) => (options[k] as readonly unknown[]).includes(given[k]))) return null
  if (pages === undefined) return { from, look: given } as never
  const edits = readEdits(each ?? {}, pages)
  if (!edits) return null
  return (Object.keys(edits).length ? { from, look: given, pages: edits } : { from, look: given }) as never
}

// A design as kept in the database; anything unreadable counts as none.
export function storedDesign<T extends Options & { looks: readonly string[] }, P extends Options>(
  options: T,
  each: P,
  text: string | null,
) {
  if (!text) return null
  try {
    return readDesign(options, JSON.parse(text), each)
  } catch {
    return null
  }
}
